import {LoadingSpinner, Text} from '@albert-io/atomic';
import {useQuestionTranslationStore} from 'client/components/QuestionTypes/shared/TranslationSection';
import supplementActions from 'client/Supplement/SupplementActions';
import supplementStore from 'client/Supplement/SupplementStore';
import {getSupplementTypeDefinition} from 'client/SupplementTypes/SupplementTypeDefinitions';
import {Map} from 'immutable';
import React, {useEffect, useMemo, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {useQuestionEditorV2Store} from 'client/EditPage/V2/QuestionEditorV2Store';

import {callAction} from '../framework';

interface SupplementRendererProps {
  id: string;
  attributes: unknown;
}

const SupplementRenderer = ({id, attributes}: SupplementRendererProps) => {
  const [isReady, setIsReady] = useState(supplementStore.hasSupplement(id));
  const [hasError, setHasError] = useState(false);
  const [supplement, setSupplement] = useState<any | null>(null);
  const currentTranslatedSupplements = useQuestionEditorV2Store((state) =>
    state.currentTranslatedSupplements()
  );

  useEffect(() => {
    const fetchOrSetSupplement = async () => {
      if (!isReady) {
        callAction((supplementActions as Record<string, symbol>).FETCH_SUPPLEMENT, id, undefined);
        const supplementPromise = supplementStore.getFetchSupplementPromise();
        try {
          await supplementPromise;
        } catch (_err) {
          setHasError(true);
        } finally {
          setIsReady(true);
        }
      } else {
        setSupplement(supplementStore.getSupplement(id));
      }
    };
    fetchOrSetSupplement();
  }, [id, isReady]);

  // translation info
  const {translatedSupplements, showTranslations, showTranslatedSupplements} =
    useQuestionTranslationStore(
      useShallow((state) => ({
        translatedSupplements: state.translatedSupplements,
        showTranslations: state.showTranslations,
        showTranslatedSupplements: state.translationSettings.supplements
      }))
    );

  const noTranslation = useMemo(() => {
    if (currentTranslatedSupplements) {
      const translatedSup = currentTranslatedSupplements.find((ts) => ts.id === id);
      return !(translatedSup?.meets_translation_requirements && translatedSup?.translate);
    }
    const translatedSup = translatedSupplements?.find((ts) => ts.id === id);
    return !(translatedSup?.meets_translation_requirements && translatedSup?.translate);
  }, [id, translatedSupplements, currentTranslatedSupplements]);

  const {content, name} = useMemo(() => {
    if (!supplement) return {content: Map(), name: ''};
    if (currentTranslatedSupplements != null) {
      const translatedSup = currentTranslatedSupplements.find((ts) => ts.id === id);
      if (translatedSup) {
        const contentMap = Map({...translatedSup.content});
        return {content: contentMap, name: translatedSup.name};
      }
    }
    if (!showTranslatedSupplements) {
      return {
        content: supplement.get('content', Map()),
        name: supplement.get('name')
      };
    }
    if (showTranslations) {
      const translatedSup = translatedSupplements?.find((ts) => ts.id === id);
      if (translatedSup) {
        const contentMap = Map({...translatedSup.content});
        return {content: contentMap, name: translatedSup.name};
      }
    }
    return {
      content: supplement.get('content', Map()),
      name: supplement.get('name')
    };
  }, [
    supplement,
    showTranslations,
    id,
    translatedSupplements,
    currentTranslatedSupplements,
    showTranslatedSupplements
  ]);

  const Renderer = useMemo(() => {
    if (!supplement) return null;
    try {
      const renderer = getSupplementTypeDefinition(supplement.get('type')).get('renderer');
      return renderer;
    } catch (_err) {
      return null;
    }
  }, [supplement]);

  if (!isReady || hasError || !supplement) {
    return (
      <div className='flex justify-center'>
        {hasError ? (
          <Text color='negative'>There was a problem loading the attached supplement.</Text>
        ) : (
          <LoadingSpinner assistiveText='Loading question supplement...' size={2} />
        )}
      </div>
    );
  }

  return (
    <Renderer
      id={supplement.getId()}
      name={name}
      content={content}
      supplement={supplement}
      attributes={attributes}
      noTranslation={noTranslation}
      showTranslationInfo={currentTranslatedSupplements != null || showTranslations}
      showTranslatedSupplements={currentTranslatedSupplements != null || showTranslatedSupplements}
    />
  );
};

export default SupplementRenderer;
